import {
  AdapterInterface,
  AdapterCdoMceInterface,
  AdapterCdoLn1Interface,
  AdapterCdoSequentInterface,
  AdapterBacnetInterface,
  BacnetBankMapping,
} from "../../../interfaces/Adapter.interface";
import { AdapterCdoOpcInterface } from "../../../interfaces/AdapterCdoOpc.interface";
import { AdapterCdoOpcUa } from "../../../interfaces/AdapterCdoOpcUa.interface";
import { AdapterCdoLobbyVision2 } from "../../../interfaces/AdapterCdoLobbyVision2.interface";
import { AdapterProtocol } from "../../../flatbuffers/generated/archive/adapter-protocol";
import {
  OPC_UA_BINARY_CDO_CONFIG,
  LOBBYVISION2_BINARY_CDO_CONFIG,
} from "../../setupWizard/editors/BinaryCdoProtocolRegistry";
import { logError } from "../../../utils/devLog";

export interface CdoExtractionResult {
  cdoMce: AdapterCdoMceInterface | null;
  cdoLn1: AdapterCdoLn1Interface | null;
  cdoSequent: AdapterCdoSequentInterface | null;
  cdoOpc: AdapterCdoOpcInterface | null;
  cdoOpcUa: AdapterCdoOpcUa | null;
  cdoLobbyVision2: AdapterCdoLobbyVision2 | null;
  cdoBacnet: AdapterBacnetInterface[] | null;
  bacnetBankMappings: BacnetBankMapping[] | null;
  cdoJsonFallback: string | null;
}

const LN1_BASED_PROTOCOLS: AdapterProtocol[] = [
  AdapterProtocol.LN1,
  AdapterProtocol.RVU,
  AdapterProtocol.CGA,
  AdapterProtocol.LOBBYVISION,
];

const JSON_FALLBACK_PROTOCOLS: AdapterProtocol[] = [
  AdapterProtocol.STM32,
  AdapterProtocol.NUMATORELAY,
  AdapterProtocol.NUMATOGPIO,
];

/**
 * Pure extraction of CDO state from an adapter record.
 *
 * Protocol-specific CDO types (MCE, LN1, Sequent, OPC) are mutually exclusive
 * — only one is populated based on `protocolType`.
 *
 * BACnet is independent: it is populated whenever `bacnetConfig` has entries,
 * regardless of protocolType. This allows adapters to have both a protocol CDO
 * (e.g. OPC) AND BACnet configuration simultaneously.
 */
export function extractCdoState(adapter: AdapterInterface): CdoExtractionResult {
  const result: CdoExtractionResult = {
    cdoMce: null,
    cdoLn1: null,
    cdoSequent: null,
    cdoOpc: null,
    cdoOpcUa: null,
    cdoLobbyVision2: null,
    cdoBacnet: null,
    bacnetBankMappings: null,
    cdoJsonFallback: null,
  };

  const proto = adapter.protocolType;
  const cdo = adapter.cdoInput;

  // CDO MCE
  // The Go backend stores MCE CDO with flat event keys because the
  // FlatBuffer schema uses flat fields, not a nested event object. The
  // frontend form uses a nested `event` sub-object (AdapterCdoMceInterface).
  // Handle both shapes: nested (from form state / future API) and flat
  // (from server GET). product_type is optional in the guard because
  // FlatBuffers omit fields with default values (0) from the serialized JSON.
  if (proto === AdapterProtocol.MCE && cdo && "idl_file" in cdo) {
    const mce = cdo as Partial<AdapterCdoMceInterface> & {
      idl_file: string;
      event_mapping_file?: string;
      event_mapping_data?: string;
    };
    const event = mce.event ?? {
      mapping_file: mce.event_mapping_file ?? "",
      mapping_data: mce.event_mapping_data ?? "",
    };
    result.cdoMce = {
      idl_file: mce.idl_file,
      product_type: mce.product_type ?? 0,
      event,
    };
  }
  // CDO LN1 / RVU / CGA / LOBBYVISION
  else if (
    LN1_BASED_PROTOCOLS.includes(proto) &&
    cdo &&
    "allowed_messages" in cdo &&
    "crc_override" in cdo &&
    "should_filter_duplicates" in cdo &&
    "terminating_zeroes_expected" in cdo
  ) {
    result.cdoLn1 = {
      allowed_messages: (cdo as AdapterCdoLn1Interface).allowed_messages,
      crc_override: (cdo as AdapterCdoLn1Interface).crc_override,
      should_filter_duplicates: (cdo as AdapterCdoLn1Interface).should_filter_duplicates,
      terminating_zeroes_expected: (cdo as AdapterCdoLn1Interface).terminating_zeroes_expected,
      // RVU-only; absent (undefined) for LN1/CGA/LOBBYVISION and when unset on RVU.
      heartbeat_kind: (cdo as AdapterCdoLn1Interface).heartbeat_kind ?? null,
    };
  }
  // CDO SEQUENT
  else if (
    proto === AdapterProtocol.SEQUENT &&
    cdo &&
    "discrete_2_message" in cdo &&
    "dev_type" in cdo &&
    (cdo as AdapterCdoSequentInterface).discrete_2_message
  ) {
    result.cdoSequent = {
      discrete_2_message: (cdo as AdapterCdoSequentInterface).discrete_2_message,
      dev_type: (cdo as AdapterCdoSequentInterface).dev_type,
    };
  }
  // CDO OPC
  else if (
    proto === AdapterProtocol.OPC &&
    cdo &&
    "sites" in cdo &&
    "locations" in cdo &&
    "groups" in cdo &&
    "lifts" in cdo &&
    "car_num_to_name" in cdo &&
    "opc_server" in cdo &&
    "hostname" in cdo &&
    "location" in cdo
  ) {
    result.cdoOpc = {
      sites: (cdo as AdapterCdoOpcInterface).sites,
      locations: (cdo as AdapterCdoOpcInterface).locations,
      groups: (cdo as AdapterCdoOpcInterface).groups,
      lifts: (cdo as AdapterCdoOpcInterface).lifts,
      car_num_to_name: (cdo as AdapterCdoOpcInterface).car_num_to_name,
      opc_server: (cdo as AdapterCdoOpcInterface).opc_server,
      hostname: (cdo as AdapterCdoOpcInterface).hostname,
      location: (cdo as AdapterCdoOpcInterface).location,
    };
  }
  // CDO OPC UA — union-heavy (Identity, NodeIdentifier, Data/Variant), so
  // config lives in the FlatBuffer binary (adapter.cdoBinary), NOT
  // cdo_input JSON. Decoded synchronously here when the binary is already
  // present on the record; AdapterEditor is responsible for fetching
  // cdoBinary (via the same /adapters/:id/cdo endpoint Modbus uses) before
  // calling extractCdoState, mirroring the Modbus binary-load pattern.
  else if (proto === AdapterProtocol.OPCUA && adapter.cdoBinary && adapter.cdoBinary.length > 0) {
    try {
      result.cdoOpcUa = OPC_UA_BINARY_CDO_CONFIG.decode(adapter.cdoBinary);
    } catch (err) {
      logError("[extractCdoState] decode OPC UA cdoBinary failed:", err);
    }
  }
  // CDO LOBBYVISION2 — a bank/lift-agnostic template (Config.LobbyVision2Template)
  // that LiftCloud expands per car/floor at CDO-generation time, so config
  // lives in the FlatBuffer binary (adapter.cdoBinary), NOT cdo_input JSON —
  // same reasoning as OPC UA above. AdapterEditor fetches cdoBinary before
  // calling extractCdoState.
  else if (
    proto === AdapterProtocol.LOBBYVISION2 &&
    adapter.cdoBinary &&
    adapter.cdoBinary.length > 0
  ) {
    try {
      result.cdoLobbyVision2 = LOBBYVISION2_BINARY_CDO_CONFIG.decode(adapter.cdoBinary);
    } catch (err) {
      logError("[extractCdoState] decode LOBBYVISION2 cdoBinary failed:", err);
    }
  }

  // MODBUS — CDO is loaded as FlatBuffer binary via a separate fetch.
  // Intentionally skipped here; AdapterEditor handles it.
  // STM32 / NUMATORELAY / NUMATOGPIO — store raw JSON for visual editors
  else if (JSON_FALLBACK_PROTOCOLS.includes(proto) && cdo) {
    try {
      result.cdoJsonFallback = JSON.stringify(cdo, null, 2);
    } catch {
      result.cdoJsonFallback = "{}";
    }
  }

  // CDO BACNET — independent of protocolType (separate if, not else-if)
  if (adapter.bacnetConfig && adapter.bacnetConfig.length > 0) {
    result.cdoBacnet = [...adapter.bacnetConfig];
  }

  // BACnet bank/lift mappings — independent of protocolType
  if (adapter.bacnetBankMappings && adapter.bacnetBankMappings.length > 0) {
    result.bacnetBankMappings = [...adapter.bacnetBankMappings];
  }

  return result;
}
